import React, { forwardRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBTypography, MDBBtn, MDBBadge, MDBInput, MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit';
import Header from './Header';
import Sidebar from '../components/Sidebar';
import { getMemberTransactions } from '../../services/memberApiServices';
import moment from 'moment';
import { useCart } from './CartContext';
import calImg from "../../components/images/calendar.svg";
import DatePicker from "react-datepicker";

const MemberCheckins = () => {
    const { getMemberId } = useCart();
    const navigate = useNavigate();
    const [memberData, setMemberData] = useState({});
    const [loading, setLoading] = useState(true);
    const [trainerFilter, setTrainerFilter] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({
        trainer: '',
        dateRange: [null, null],
    });

    useEffect(() => {
        const fetchMemberData = async () => {
            try {
                if (!localStorage.getItem("loggedMemberInfo") && !localStorage.getItem("memberinfo")) {
                    navigate('/memberlogin', { state: { path: window.location.pathname } })
                }
                setLoading(true);
                const response = await getMemberTransactions(getMemberId());
                if (response && response !== "false") {
                    const data = await response.json();
                    if (data && data.length > 0) {
                        setMemberData(data[0]);
                    }
                }
            } catch (error) {
                console.error("Error fetching member data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMemberData();
    }, [getMemberId]);

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const durationMs = endTime - startTime;

        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

        return `${hours}h ${minutes}m`;
    };

    const applyFilters = () => {
        setAppliedFilters(prev => ({
            ...prev,
            trainer: trainerFilter
        }));
    };

    const filteredCheckins = memberData.attendance?.filter(session => {
        const [startDate, endDate] = appliedFilters.dateRange;
        const sessionDate = new Date(session.intime);

        // Date range filter
        let matchesDate = true;
        if (startDate && endDate) {
            // Set end date to end of day for inclusive comparison
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);

            matchesDate = sessionDate >= startDate && sessionDate <= endOfDay;
        } else if (startDate) {
            matchesDate = sessionDate >= startDate;
        } else if (endDate) {
            // Set end date to end of day for inclusive comparison
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);

            matchesDate = sessionDate <= endOfDay;
        }

        // Trainer filter
        const matchesTrainer = appliedFilters.trainer
            ? session.trainer?.toLowerCase().includes(appliedFilters.trainer.toLowerCase())
            : true;

        return matchesDate && matchesTrainer;
    });

    const uniqueTrainers = [...new Set(
        memberData.attendance?.map(session => session.trainer).filter(Boolean)
    )];

    const CalenderInput = forwardRef(({ value, onClick }, ref) => (
        <>
            <span className="form-control select-input placeholder-active me-3 shadow-1" onClick={onClick}>
                <p className="d-inline m-0 me-2">
                    {value === "" ? "Custom Range" : value}
                </p>
                <img src={calImg} ref={ref} alt="Custom Range" title="Select Custom Range" />
            </span>
        </>
    ));

    const handleDateRangeChange = (dates) => {
        setAppliedFilters(prev => ({
            ...prev,
            dateRange: dates
        }));
    };

    const clearFilters = () => {
        setTrainerFilter('');
        setAppliedFilters({
            trainer: '',
            dateRange: [null, null]
        });
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <Sidebar active={"5"} />
                <div className="main-content">
                    <Header title={"Check-ins"} />
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p>Loading check-in data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <Sidebar active={"5"} />
            <div className="main-content">
                <Header title={"Check-ins"} />
                <div className="d-flex align-items-center mb-3 ms-2">
                    <a href="/memberprofile" className="text-decoration-none p-0" style={{ fontSize: '14px' }}>Profile</a>
                    <i className="fa-solid fa-angle-right mx-2 text-muted"></i>
                    <span className="text-muted">Check-ins</span>
                </div>
                <div className="dashboard-content" >
                    {/* Fixed Filters Section */}
                    <MDBCard className="shadow-sm rounded h-100">
                        <MDBCardBody className='d-flex flex-column h-100'>
                            <div className="row g-3 align-items-end">
                                <MDBCol md="4" className='m-0'>
                                    <div className="form-group">
                                        <label className="form-label mb-1">Date Range</label>
                                        <DatePicker
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={true}
                                            startDate={appliedFilters.dateRange[0]}
                                            endDate={appliedFilters.dateRange[1]}
                                            onChange={handleDateRangeChange}
                                            isClearable={true}
                                            customInput={<CalenderInput />}
                                            placeholderText="Select date range"
                                        />
                                    </div>
                                </MDBCol>
                                <MDBCol md="4" className='m-0'>
                                    <div className="form-group">
                                        <label className="form-label mb-1">Filter by trainer</label>
                                        <select
                                            className="form-select"
                                            value={trainerFilter}
                                            onChange={(e) => setTrainerFilter(e.target.value)}
                                        >
                                            <option value="">All Trainers</option>
                                            {uniqueTrainers?.map(trainer => (
                                                <option key={trainer} value={trainer}>{trainer}</option>
                                            ))}
                                        </select>
                                    </div>
                                </MDBCol>
                                <MDBCol md="4" className="d-flex justify-content-end m-0">

                                    <button
                                        className="filter-btn cancel-btn me-2"
                                        onClick={clearFilters}
                                    >
                                        <i className="fa-solid fa-eraser me-2"></i> Clear
                                    </button>

                                    <button
                                        className="filter-btn apply-btn"
                                        onClick={applyFilters}
                                    >
                                        <i className="fa-solid fa-filter me-2"></i>   Apply
                                    </button>

                                </MDBCol>
                            </div>
                            <div className="table-responsive-mobile">
                                <MDBTable className="mb-0 check-in-out-table mt-3 flex-grow-1">
                                    <MDBTableHead>
                                        <tr>
                                            <th scope="col" style={{ width: '25%' }}>
                                                <i className="fa-solid fa-user-tie me-2"></i>
                                                Trainer
                                            </th>
                                            <th scope="col" style={{ width: '25%' }}>
                                                <i className="fa-solid fa-arrow-right-to-bracket me-2"></i>
                                                Check-in
                                            </th>
                                            <th scope="col" style={{ width: '25%' }}>
                                                <i className="fa-solid fa-arrow-right-from-bracket me-2"></i>
                                                Check-out
                                            </th>
                                            <th scope="col" style={{ width: '25%' }}>
                                                <i className="fa-regular fa-clock me-2"></i>
                                                Duration
                                            </th>
                                        </tr>
                                    </MDBTableHead>
                                    <MDBTableBody>
                                        {filteredCheckins?.length > 0 ? (
                                            filteredCheckins.map((session) => (
                                                <tr key={session._id} className="border-top">
                                                    <td style={{ width: '25%' }}>
                                                        {session.trainer || (
                                                            <>
                                                                <i className="fa-solid fa-user me-2 text-secondary"></i>
                                                                Self Check-in
                                                            </>
                                                        )}
                                                    </td>
                                                    <td style={{ width: '25%' }}>
                                                        <i className="fa-solid fa-arrow-right-to-bracket me-2 mob-icon"></i>  {moment(new Date(session.intime)).format('YYYY-MM-DD hh:mm A')}
                                                    </td>
                                                    <td style={{ width: '25%' }}>
                                                        <i className="fa-solid fa-arrow-right-from-bracket me-2 mob-icon"></i>    {session.outtime ?
                                                            moment(new Date(session.outtime)).format('YYYY-MM-DD hh:mm A') :
                                                            <MDBBadge color="warning" pill className="text-dark">
                                                                <i className="fa-solid fa-spinner me-1"></i>
                                                                In Progress
                                                            </MDBBadge>
                                                        }
                                                    </td>
                                                    <td style={{ width: '25%' }}>
                                                        <i className="fa-regular fa-clock me-2 mob-icon"></i>      {session.outtime ? (
                                                            calculateDuration(session.intime, session.outtime)
                                                        ) : '--'}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <i className="fa-solid fa-magnifying-glass fa-2x mb-3 text-muted"></i>
                                                    <p>No check-ins found matching your filters</p>
                                                    <MDBBtn color="link" onClick={clearFilters}>
                                                        <i className="fa-solid fa-eraser me-2"></i> Clear filters
                                                    </MDBBtn>
                                                </td>
                                            </tr>
                                        )}
                                    </MDBTableBody>
                                </MDBTable>
                            </div>
                        </MDBCardBody>
                    </MDBCard>
                </div>
            </div>
        </div>
    );
};

export default MemberCheckins;